import { Injectable } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { from, Observable, of, Subscriber, TeardownLogic } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppWrapper } from 'src/app/wrappers/app.wrapper';
import { DeviceWrapper } from 'src/app/wrappers/device.wrapper';
import { EnvironmentWrapper } from 'src/app/wrappers/environment.wrapper';
import { MSAppInfo } from './utils/app.utils';

type AppStateChangeListener = (subscriptionListener: () => void) => Promise<PluginListenerHandle>
type SubscribeHandler = (subscriber: Subscriber<void>) => TeardownLogic

@Injectable({
  providedIn: 'root'
})
export class AppAdapter {
  constructor(
    private appWrapper: AppWrapper,
    private deviceWrapper: DeviceWrapper,
    private environmentWrapper: EnvironmentWrapper
  ) {}

  getAppInfo(): Observable<MSAppInfo> {
    const deviceObservable = from(this.deviceWrapper.getDeviceInfo());
    const appInfoObservable = deviceObservable.pipe(switchMap((deviceInfo) => this.getAppInfoForPlatform(deviceInfo.platform)));

    return appInfoObservable;
  }

  getAppResumeStream(): Observable<void> {
    const addAppResumeListener = (listener: () => void) => this.appWrapper.addResumeListener(listener)
    const appResumeObserver = this.subscribeToStateChangeListener(addAppResumeListener);
    const appResumeObservable = new Observable<void>(appResumeObserver);

    return appResumeObservable;
  }

  private getAppInfoForPlatform(platform: string): Observable<MSAppInfo> {
    if (platform === 'web') {
      return this.getAppInfoForBrowser();
    } else {
      return this.getAppInfoForMobile();
    }
  }

  private getAppInfoForBrowser(): Observable<MSAppInfo> {
    const environment = this.environmentWrapper.getEnvironment();
    const appInfoObservable: Observable<MSAppInfo> = of({
      name: environment.browserAppConfig.appName,
      id: environment.browserAppConfig.appId,
      version: environment.browserAppConfig.version,
      build: environment.browserAppConfig.distributionVersion
    });

    return appInfoObservable;
  }

  private getAppInfoForMobile(): Observable<MSAppInfo> {
    const appInfoPromise = new Promise<MSAppInfo>(async (resolve) => {
      const appInfo = await this.appWrapper.getAppInfo();
      const msAppInfo: MSAppInfo = {
        name: appInfo.name,
        id: appInfo.id,
        version: appInfo.version,
        build: appInfo.build
      };

      resolve(msAppInfo);
    });

    const appInfoObservable = from(appInfoPromise);
    return appInfoObservable;
  }

  private subscribeToStateChangeListener(stateChangeListener: AppStateChangeListener): SubscribeHandler {
    const subscribeFunc = (subscriber: Subscriber<void>) => {
      const listenerFunc = () => subscriber.next();
      stateChangeListener(listenerFunc);
    };

    return subscribeFunc;
  }

}
